import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Container, Title, Text, Button, Paper, Group, Stack, Modal,
  TextInput, PasswordInput, Table, Badge, Loader, ActionIcon, Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ArrowLeft, UserPlus, Trash2, Users } from 'lucide-react'
import { rosterApi, Roster, RosterStudent } from '../../api/roster'

export function RosterDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [roster, setRoster] = useState<Roster | null>(null)
  const [students, setStudents] = useState<RosterStudent[]>([])
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [userName, setUserName] = useState('')
  const [initialPassword, setInitialPassword] = useState('')
  const [adding, setAdding] = useState(false)
  const [removingId, setRemovingId] = useState<number | null>(null)

  const load = () => {
    rosterApi.get(Number(id))
      .then((res) => {
        setRoster(res.data.roster)
        setStudents(res.data.students)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load roster', color: 'red' }))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [id])

  const resetAddForm = () => {
    setEmail('')
    setUserName('')
    setInitialPassword('')
  }

  const handleAddStudent = async () => {
    if (!email.trim()) return
    const uname = userName.trim()
    const pw = initialPassword
    if (uname && pw.length < 6) {
      notifications.show({
        title: 'Password too short',
        message: 'Initial password must be at least 6 characters when creating a new account.',
        color: 'red',
      })
      return
    }
    if (pw && !uname) {
      notifications.show({
        title: 'Username required',
        message: 'Enter a username when setting an initial password for a new student.',
        color: 'red',
      })
      return
    }
    if (uname && !pw) {
      notifications.show({
        title: 'Password required',
        message: 'Enter an initial password when creating a new student account.',
        color: 'red',
      })
      return
    }
    setAdding(true)
    try {
      const res = await rosterApi.addStudent(Number(id), {
        student_email: email.trim(),
        ...(uname ? { user_name: uname } : {}),
        ...(pw ? { initial_password: pw } : {}),
      })
      const created = res.data?.account_created
      notifications.show({
        title: 'Added',
        message: created
          ? 'Student account created and added to this roster. They can sign in with email or username and the password you set.'
          : 'Student added to roster.',
        color: 'green',
      })
      resetAddForm()
      setAddOpen(false)
      load()
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed to add student', color: 'red' })
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (studentId: number, name: string | null) => {
    if (!confirm(`Remove ${name || 'this student'} from the roster?`)) return
    setRemovingId(studentId)
    try {
      await rosterApi.removeStudent(Number(id), studentId)
      notifications.show({ title: 'Removed', message: 'Student removed.', color: 'orange' })
      setStudents((prev) => prev.filter((s) => s.student_id !== studentId))
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed to remove', color: 'red' })
    } finally {
      setRemovingId(null)
    }
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  if (!roster) {
    return (
      <Container py={32}>
        <Alert color="red">Roster not found.</Alert>
        <Button mt={12} variant="subtle" onClick={() => navigate('/tutor/rosters')}>
          Back to Rosters
        </Button>
      </Container>
    )
  }

  return (
    <Container size="md" py={32}>
      <Group mb={24}>
        <ActionIcon variant="subtle" color="gray" onClick={() => navigate('/tutor/rosters')}>
          <ArrowLeft size={20} />
        </ActionIcon>
        <Box style={{ flex: 1 }}>
          <Title order={2} c="var(--sat-text)">{roster.name}</Title>
          {roster.description && <Text size="sm" c="var(--sat-text-soft)">{roster.description}</Text>}
        </Box>
        <Button
          leftSection={<UserPlus size={16} />}
          color="grapefruit"
          onClick={() => setAddOpen(true)}
        >
          Add Student
        </Button>
      </Group>

      <Paper shadow="xs" radius="md" p={0} style={{ overflow: 'hidden' }}>
        <Group px={20} py={14} style={{ background: 'var(--sat-bg)', borderBottom: '1px solid var(--sat-border-soft)' }}>
          <Users size={18} color="var(--sat-green)" />
          <Text fw={600} c="var(--sat-text)" size="sm">{students.length} Student{students.length !== 1 ? 's' : ''}</Text>
        </Group>

        {students.length === 0 ? (
          <Stack align="center" gap={8} py={40}>
            <Text c="var(--sat-text-faint)" size="sm">No students in this roster yet.</Text>
            <Button size="xs" color="grapefruit" onClick={() => setAddOpen(true)}>
              Add First Student
            </Button>
          </Stack>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Enrolled</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {students.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    <Text size="sm" fw={500} c="var(--sat-text)">{s.student_name || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="var(--sat-text-subtle)">{s.student_email || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="var(--sat-text-faint)">
                      {s.enrolled_date ? new Date(s.enrolled_date).toLocaleDateString() : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={s.is_active ? 'green' : 'gray'} variant="light" size="sm">
                      {s.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      loading={removingId === s.student_id}
                      onClick={() => handleRemove(s.student_id, s.student_name)}
                    >
                      <Trash2 size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Modal
        opened={addOpen}
        onClose={() => { setAddOpen(false); resetAddForm() }}
        title={<Text fw={700} c="var(--sat-text)">Add Student</Text>}
        radius="md"
      >
        <Stack gap={16}>
          <Text size="sm" c="var(--sat-text-subtle)">
            If the student already has an account, enter only their email. Otherwise add email, display username, and a temporary password so they can sign in (email or username + password).
          </Text>
          <TextInput
            label="Student email"
            placeholder="student@example.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddStudent() }}
          />
          <TextInput
            label="Username (for new students)"
            placeholder="e.g. jsmith"
            description="Shown on the platform; used to sign in if they prefer not to use email."
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
          />
          <PasswordInput
            label="Initial password (for new students)"
            placeholder="At least 6 characters"
            description="Share this with the student so they can log in."
            value={initialPassword}
            onChange={(e) => setInitialPassword(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={() => { setAddOpen(false); resetAddForm() }}>Cancel</Button>
            <Button color="grapefruit" loading={adding} onClick={handleAddStudent}>
              Add Student
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  )
}
